var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useState } from "react";
import { BigNumber } from "ethers";
import { AssetsList } from "../store/types";
import { impersonateAddress } from "../utils/development";
import { Chains, isNativeToken } from "../constants/constants";
import { getERC20Token } from "./useERC20Token";
import { isProduction } from "../utils/env";
import { usePendingTransactions } from "./pendingTransactionsContext";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import useWeb3Wallet from "./useWeb3Wallet";
import { isEVMChain, isSolanaChain } from "../utils/chains";
import { useChain } from "./chainContext";
import { getChainByAsset } from "../utils/asset";
export var defaultUserAssetBalanceData = {
    data: Object.fromEntries(AssetsList.map(function (asset) { return [asset, BigNumber.from(0)]; })),
    loading: true,
};
var useFetchAssetBalanceData = function (_a) {
    var _b = _a === void 0 ? { poll: true, pollingFrequency: 20000 } : _a, poll = _b.poll, pollingFrequency = _b.pollingFrequency;
    var _c = useState(defaultUserAssetBalanceData), data = _c[0], setData = _c[1];
    var _d = useWeb3Wallet(), ethereumProvider = _d.ethereumProvider, chainId = _d.chainId, active = _d.active, web3Account = _d.account;
    var account = impersonateAddress ? impersonateAddress : web3Account;
    var transactionsCounter = usePendingTransactions().transactionsCounter;
    var connection = useConnection().connection;
    var publicKey = useWallet().publicKey;
    var chain = useChain()[0];
    var doMulticall = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var responses;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!isProduction()) {
                        console.time("Asset Balance Data Fetch"); // eslint-disable-line
                    }
                    if (!active) {
                        setData(__assign(__assign({}, defaultUserAssetBalanceData), { loading: false }));
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, Promise.all(AssetsList.map(function (asset) { return __awaiter(void 0, void 0, void 0, function () {
                            var defaultResponse, _a, balance, token, balance, tokenBalance;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0:
                                        defaultResponse = {
                                            asset: asset,
                                            balance: undefined,
                                        };
                                        _a = getChainByAsset(asset);
                                        switch (_a) {
                                            case Chains.BSC: return [3 /*break*/, 1];
                                            case Chains.Ethereum: return [3 /*break*/, 1];
                                            case Chains.Avalanche: return [3 /*break*/, 1];
                                            case Chains.Solana: return [3 /*break*/, 6];
                                        }
                                        return [3 /*break*/, 9];
                                    case 1:
                                        if (!(isEVMChain(chain) && ethereumProvider)) return [3 /*break*/, 5];
                                        if (!isNativeToken(asset)) return [3 /*break*/, 3];
                                        return [4 /*yield*/, ethereumProvider.getBalance(account)];
                                    case 2:
                                        balance = _b.sent();
                                        return [2 /*return*/, { asset: asset, balance: balance }];
                                    case 3:
                                        token = getERC20Token(ethereumProvider, asset.toLowerCase(), chainId);
                                        if (!token) return [3 /*break*/, 5];
                                        return [4 /*yield*/, (isNativeToken(asset)
                                                ? ethereumProvider.getBalance(account)
                                                : token.balanceOf(account))];
                                    case 4:
                                        balance = _b.sent();
                                        // console.log('balance:', balance)
                                        return [2 /*return*/, { asset: asset, balance: balance }];
                                    case 5: return [2 /*return*/, defaultResponse];
                                    case 6:
                                        if (!isSolanaChain(chain)) return [3 /*break*/, 8];
                                        if (!isNativeToken(asset)) return [3 /*break*/, 8];
                                        return [4 /*yield*/, connection.getBalance(publicKey)];
                                    case 7:
                                        tokenBalance = _b.sent();
                                        return [2 /*return*/, {
                                                asset: asset,
                                                balance: BigNumber.from(tokenBalance),
                                            }];
                                    case 8: return [2 /*return*/, defaultResponse];
                                    case 9: return [2 /*return*/, defaultResponse];
                                }
                            });
                        }); }))];
                case 1:
                    responses = _a.sent();
                    setData(function (prevData) { return ({
                        data: Object.fromEntries(responses.map(function (_a) {
                            var asset = _a.asset, balance = _a.balance;
                            return [
                                asset,
                                /**
                                 * We fall back to previous value if balance is undefined
                                 */
                                balance || prevData.data[asset],
                            ];
                        })),
                        loading: false,
                    }); });
                    if (!isProduction()) {
                        console.timeEnd("Asset Balance Data Fetch"); // eslint-disable-line
                    }
                    return [2 /*return*/];
            }
        });
    }); }, [
        account,
        active,
        chain,
        chainId,
        ethereumProvider,
        connection,
        publicKey,
    ]);
    /**
     * Fetch on first load and transaction success
     */
    useEffect(function () {
        doMulticall();
    }, [doMulticall, transactionsCounter]);
    /**
     * Schedule polling
     * We still need polling as user might perform transaction that changes their asset balance
     * (buy/sell) outside of app
     */
    useEffect(function () {
        var pollInterval = undefined;
        if (poll) {
            pollInterval = setInterval(doMulticall, pollingFrequency);
        }
        return function () {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [doMulticall, poll, pollingFrequency]);
    return data;
};
export default useFetchAssetBalanceData;
//# sourceMappingURL=useFetchAssetBalanceData.js.map